import type { ApiError } from '@/api/client.types';

import type { SupportCalendarDate, SupportCourseRole } from './supportConsoleTypes';

// The longest search the backend accepts (supportQueryMaxLength).
export const SUPPORT_QUERY_MAX_LENGTH = 320;

// The words the rest of the app uses for course staff.
export const SUPPORT_ROLE_LABELS: Record<SupportCourseRole, string> = {
  assistant: 'Assistant',
  primary_instructor: 'Main instructor',
  secondary_instructor: 'Additional instructor',
  student: 'Student',
  unknown: 'Unknown',
};

// A moment in time, as a date in the viewer's own time zone.
export function formatSupportDate(date: Date | null): string {
  return date
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date)
    : 'Unknown';
}

export function formatSupportDateTime(date: Date | null): string {
  return date
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
    : 'Unknown';
}

// A course's start or end date. Built from the stored day, month and year as
// a local date, so no time zone can move it to the day before.
export function formatSupportCalendarDate(date: SupportCalendarDate | null): string {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
    new Date(date.year, date.month - 1, date.day),
  );
}

export function pluralize(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

const GENERIC_FAILURE = 'Something went wrong. Please try again.';

// What to tell support staff when a call fails. The console's own refusals
// (400, 403, 404, 409) carry a short message written for a person; anything
// else, and anything long enough to be an error page, gets the generic line.
export function supportErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return GENERIC_FAILURE;
  }

  const { kind, message, status } = error as Partial<ApiError>;

  if (kind === 'network') {
    return 'Could not reach Kudu. Check your connection and try again.';
  }

  const text = typeof message === 'string' ? message.trim() : '';
  const isRefusal = status === 400 || status === 403 || status === 404 || status === 409;

  return isRefusal && text !== '' && text.length <= 200 ? text : GENERIC_FAILURE;
}
